import { FunctionComponent } from "react";

import { faArrowRotateRight, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import Email from "@assets/icons/emailIcon.svg?url";
import Checked from "@assets/icons/checked.svg?url";
import Failed from "@assets/icons/failed.svg?url";
import Refresh from "@assets/icons/refresh.svg?url";

import { Icon } from "@/components";

export interface EmailIconProps {
	status?:
		| "checked"
		| "failed"
		| "refresh"
		| "retry"
		| "searching"
		| "bounced-valid"
		| "bounced-invalid";
}

/**
 * Email envelope with a corner status badge.
 *
 * Bounced states use a triangle-exclamation badge to set them apart from a never-tried "failed":
 * - `bounced-valid`   (amber) — the address bounced but still verifies; the bounce may be temporary.
 * - `bounced-invalid` (red)   — the address bounced and no longer verifies; treat it as dead.
 *
 * `retry` (amber circular arrow) means a lookup was tried but could not finish — unlike `refresh`
 * (the blue "never searched" badge), it must not read as "nobody has looked yet" (PREC-5035).
 */
export const EmailIcon: FunctionComponent<EmailIconProps> = ({ status }) => {
	return (
		<div className="relative mr-1 flex h-[20px] w-[20px] items-center justify-center">
			{status === "checked" && (
				<img alt="checked" className="absolute right-[-1px] top-[-1px]" src={Checked} />
			)}
			{status === "failed" && (
				<img alt="failed" className="absolute right-[-1px] top-[-1px]" src={Failed} />
			)}
			{status === "refresh" && (
				<img alt="refresh" className="absolute right-[-1px] top-[-1px]" src={Refresh} />
			)}
			{status === "retry" && (
				<Icon
					aria-label="retry"
					className="absolute right-[-2px] top-[-2px] h-3 w-3 text-status-warning"
					icon={faArrowRotateRight}
				/>
			)}
			{status === "searching" && (
				<img
					alt="searching"
					className="absolute right-[-1px] top-[-1px] animate-spin "
					src={Refresh}
				/>
			)}
			{status === "bounced-valid" && (
				<Icon
					aria-label="bounced-valid"
					className="absolute right-[-2px] top-[-2px] h-3 w-3 text-status-warning"
					icon={faTriangleExclamation}
				/>
			)}
			{status === "bounced-invalid" && (
				<Icon
					aria-label="bounced-invalid"
					className="absolute right-[-2px] top-[-2px] h-3 w-3 text-status-error"
					icon={faTriangleExclamation}
				/>
			)}
			<img alt="email" className="h-[20px] w-[20px]" src={Email} />
		</div>
	);
};
